import api from "app/lib/api";
import cacheApi from "app/lib/cacheApi";
import { formatPreviousReportAlerts } from "services/transmissions";
import { BASE_URL } from "static/constants";
import endpoints from "static/endpoints";
import { getFormattedDate } from "utils/date";
import formatDate from "utils/formatDate";
import { interpolate } from "utils/string";
import { getCurrentUserRole } from "utils/currentUser.util";
import {
  encodeHospitals,
  resolveReportsBasePath,
} from "utils/transmissionTable.query";

export const getPatientReports = async ({ url, signal }) => {
  const { data, meta } = await api.get(url, { signal });

  return { data, meta };
};

export const reportsCount = async ({
  year,
  physicians,
  hospitals,
  isMyPatientsFilter,
  patch,
}) => {
  let url = `${resolveReportsBasePath(getCurrentUserRole())}/count`;

  const params = [
    physicians?.length && `physicians=${physicians}`,
    hospitals?.length && `hospitals=${encodeHospitals(hospitals)}`,
    isMyPatientsFilter && "isMyPatientsFilter=true",
    year && `year=${year}`,
    // The count is scoped the same way as the list, so the tab chips agree with the grid.
    patch && "patch=true",
  ].filter(Boolean);

  if (params.length) url += `?${params.join("&")}`;

  const { data } = await api.get(url);

  return data;
};

export const changeNextSend = async (patientId, nextSendDate) => {
  const formattedDate = formatDate(nextSendDate, "YYYY-MM-DD");
  let url = `${endpoints.patient.nextSend}/${patientId}/${formattedDate}`;

  const data = await api.put(url);
  return data;
};

export const updateNextSend = async (payload) => {
  let url = `${endpoints.patient.updateNextSend}`;
  const data = await api.put(url, payload);
  return data;
};

export const downloadBulkReports = async (reports) => {
  let url = `${endpoints.transmissions}/${endpoints.transmission.bulkDownload}`;

  return await api(url, {
    method: "POST",
    responseType: "blob",
    data: { reports },
  });
};

export const processBulkReports = async (
  reports,
  view,
  processState,
  toReturn,
  isSign,
  isClinicProcessedUnprocess,
  shouldSendLetter,
  isArchive
) => {
  let payload = {
    reports,
    evoke: false,
    clinicActive: false,
    clinicForReview: false,
    clinicProcessed: false,
    isArchive,
  };

  if (shouldSendLetter) {
    payload["shouldSendLetter"] = true;
  }

  if (toReturn) {
    payload["returned"] = true;

    if (isSign) payload["undoSign"] = true;
    if (isClinicProcessedUnprocess) {
      payload["undoClinicProcessedUnprocess"] = true;
    }
  }

  if (view === "processed" || view === "active") {
    payload["evoke"] = true;
  } else if (view === "clinic") {
    payload["clinicActive"] = true;
  } else if (view === "clinic-review") {
    payload["clinicForReview"] = true;
  } else {
    payload["clinicProcessed"] = true;
  }

  let url = `${endpoints.transmissions}/${processState}-reports`;

  const response = await api.put(url, payload);

  return response;
};

export const bulkArchiveReports = async (reports, processState, isUndo) => {
  let payload = {
    reports,
    isUndo,
  };

  const response = await api.put(
    endpoints.transmission?.[`${processState}Reports`],
    payload
  );

  return response;
};

export const signBulkReports = async (reports) => {
  let url = `${endpoints.transmissions}/${endpoints.transmission.signReports}`;

  const data = await api.put(url, {
    reports,
  });

  return data;
};

export const billBulkReports = async (reports) => {
  let url = `${endpoints.transmissions}/${endpoints.transmission.billReports}`;

  const data = await api.put(url, {
    reports,
  });

  return data;
};

export const reportById = async (reportId, isReportDetailModal) => {
  let url = `${endpoints.transmission.list}/${reportId}`;

  if (reportId && isReportDetailModal) {
    const { data } = await api.get(url);

    return data;
  }
};

export const patientAlertBanner = async (serialNumber) => {
  let url = `${endpoints.transmission.reportBanners}/${serialNumber}`;

  if (serialNumber) {
    const { data } = await api.get(url);

    return data;
  }
};

export const fetchBillingHistory = async (reportId) => {
  let url = interpolate(endpoints.patient.billingHistoryV2, { reportId });

  if (reportId) {
    const { data } = await api.get(url);

    return data;
  }
};

export const updateNurseFlag = async ({ reportId, status, account }) => {
  let url = `${endpoints.transmission.list}/nurse-flagging/${reportId}/${status}`;

  if (reportId) {
    const response = await api.put(url, {}, { headers: { account } });

    return response;
  }
};

export const changeAlertStatus = async (reportId, status) => {
  let url = `${endpoints.transmissions}/report/alert/${reportId}/${status}`;

  if (reportId) {
    const response = await api.put(url);

    return response;
  }
};

export const changeAnticoagStatus = async (
  patientId,
  status,
  account,
  refusesOAC
) => {
  let url = interpolate(endpoints.patient.updateAnticoaStatus, {
    patientId,
  });

  if (patientId) {
    const response = await api.put(
      url,
      { status, refusesOAC },
      { headers: { account } }
    );

    return response;
  }
};

export const updateAlertStatus = async (reportId, status) => {
  let url = `${endpoints.transmissions}/report/alert-status/${reportId}/${status}`;

  if (reportId) {
    const response = await api.put(url);

    return response;
  }
};

export const sendOruDft = async (reportId) => {
  let url = `${BASE_URL}/hl7job/v2/retry/${reportId}`;

  if (reportId) {
    const response = await api.put(url);

    return response;
  }
};

export const sendOruForIntergyAccount = async (reportId) => {
  const url = `${BASE_URL}/v2/apiJob/retry/${reportId}`;
  const response = await api.put(url);
  return response;
};

export const sendReportToSFTPInterface = async (reportId) => {
  const url = `${BASE_URL}/v2/sftp/${reportId}`;
  const response = await api.put(url);
  return response;
};

export const pdfReport = async ({
  reportId,
  isFetchingCoverPage,
  isHFReport,
  enableCache,
  view,
}) => {
  let url = `${BASE_URL}/v2/transmissions/report/${reportId}`;

  if (isFetchingCoverPage || isHFReport) {
    url +=
      "?" +
      [isFetchingCoverPage && `coverPage=true`, isHFReport && `isHF=true`]
        .filter(Boolean)
        .join("&");
  }

  if (view) {
    url += "?view=" + view;
  }

  if (reportId) {
    const requestOptions = {
      method: "GET",
      responseType: "blob",
      cache: enableCache,
    };
    const response = await cacheApi(url, requestOptions);

    return response;
  }
};

export const saveReportTask = async (
  task,
  reportId,
  patientId,
  account,
  isEditTask,
  patientSerialNumber,
  patientName
) => {
  const { taskId, status, description } = task;

  let url = `${BASE_URL}/task`;
  if (isEditTask) url += `/${taskId}`;

  const payload = {
    patientId,
    reportId,
    description: description,
    status: status ?? "To Do",
    account,
    patientSerialNumber,
    patientName,
  };

  if (Object.hasOwn(task, "audited_report")) {
    payload["audited_report"] = task.audited_report;
  }

  const config = {
    headers: { account },
    method: taskId ? "PUT" : "POST",
    data: payload,
  };

  if (reportId) {
    return await api(url, config);
  }
};

export const previousReports = async (reportId, isReport) => {
  const url = `${BASE_URL}/v2/transmission/previous-reports/${reportId}`;

  if (reportId && isReport) {
    const { data } = await api.get(url);

    return data;
  }
};

export const previousAlerts = async (
  reportId,
  alert,
  date,
  isPreviousAlert
) => {
  let url = `${BASE_URL}/v2/transmission/previous-alerts/${reportId}`;

  if (!date || date === "All") {
    date = null;
  } else {
    date = getFormattedDate(date, "MMMM_YYYY");
  }
  if (!alert || alert === "All") {
    alert = null;
  }
  const params = { alert: alert, date: date };

  if (reportId && isPreviousAlert) {
    const { data } = await api.get(url, { params });

    const formattedResponse = formatPreviousReportAlerts(data);

    return formattedResponse;
  }
};

export const downloadReportFile = async (reportId, filters) => {
  let url = `${BASE_URL}/v2/transmissions/report/${reportId}`;

  const { isFetchingCoverPage, isHFReport } = filters || {};

  if (isFetchingCoverPage || isHFReport) {
    url +=
      "?" +
      [isFetchingCoverPage && `coverPage=true`, isHFReport && `isHF=true`]
        .filter(Boolean)
        .join("&");
  }

  if (reportId) {
    return await api(url, {
      method: "GET",
      responseType: "blob",
    });
  }
};

export const tasksByReportId = async (
  reportId,
  account,
  showAuditedReport = false
) => {
  let url = `${BASE_URL}/tasks/report/${reportId}`;

  if (showAuditedReport) {
    url += `?showAuditedReport=true`;
  }

  if (reportId) {
    const { data: tasks } = await api.get(url, { headers: { account } });

    return tasks;
  }
};

export const updateTransmissionReport = async (reportId, payload) => {
  let url = interpolate(endpoints.transmission.updateTransmissionReport, {
    reportId,
  });
  const response = api.put(url, payload);
  return response;
};

export const upsertEHRNotes = async (reportId, payload) => {
  let url = interpolate(endpoints.transmission.ehrNotes, { reportId });
  const response = api.put(url, payload);

  return response;
};

export const unsignBulkReports = async (reports) => {
  const data = await api.put(endpoints.transmission.unsignReports, {
    reports,
  });

  return data;
};

export const updateLaaoDevice = async (patientId, status) => {
  let url = interpolate(endpoints.patient.updateLaaoDevice, {
    patientId,
  });

  if (patientId) {
    const response = await api.put(url, { status });

    return response;
  }
};

export const fetchReportNoteSuggestions = async (
  reportId,
  noteSuggestionEnabled
) => {
  const url = interpolate(endpoints.reportNoteSuggestions.getByReportId, {
    reportId,
  });

  if (reportId && noteSuggestionEnabled) {
    const { data } = await api.get(url);

    return data;
  }
};

export const updateIDTF = async (patientId, status) => {
  if (!patientId) {
    return;
  }

  let url = interpolate(endpoints.patient.updateIDTF, {
    patientId,
  });

  const response = await api.patch(url, { status });
  return response;
};

export const updateAuditStatus = async (reportId, status) => {
  let url = interpolate(endpoints.transmission.updateAuditStatus, {
    reportId,
  });

  if (reportId) {
    return await api.patch(url, { status });
  }
};

export const assignPhysicianToReport = async (
  reportId,
  physicianId,
  assignToPatient
) => {
  let url = interpolate(endpoints.transmission.assignPhysicianToReport, {
    reportId,
  });

  if (reportId) {
    return await api.put(url, { physicianId, assignToPatient });
  }
};
